/**
 * Callback registry for API interceptors.
 * Breaks circular dependency between transport and stores.
 *
 * PATTERN: Stores register these callbacks after initialization (rootStore constructor).
 * Interceptors call getInterceptorCallbacks() instead of importing stores directly.
 */

export interface InterceptorCallbacks {
  /** Device ID for all authenticated requests */
  getDeviceId: () => string | null;

  /** Server-issued session ID — sent as x-session-id header on native */
  getServerSessionId: () => string | null;

  /** Whether user is currently logged in */
  isUserLoggedIn: () => boolean;

  /** Logout the user (called when 401/410 body has recovery === 'reauth') */
  logoutUser: () => void;

  /**
   * Store a new server session ID received in x-session-id response header.
   * Called by sessionIdExtractionInterceptor on every login/verify-email/signup response.
   */
  storeServerSessionId: (sessionId: string) => Promise<void>;
}

let callbacks: InterceptorCallbacks | null = null;

/**
 * Register callbacks for API interceptors.
 * MUST be called from rootStore constructor after all stores are initialized.
 */
export function registerInterceptorCallbacks(cb: InterceptorCallbacks): void {
  callbacks = cb;
}

/**
 * Get registered interceptor callbacks.
 * Throws if not yet registered — fail fast for misconfiguration.
 */
export function getInterceptorCallbacks(): InterceptorCallbacks {
  if (!callbacks) {
    throw new Error(
      'Interceptor callbacks not registered. Call registerInterceptorCallbacks() from rootStore constructor.',
    );
  }
  return callbacks;
}
